import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import confetti from 'canvas-confetti';

interface WebPaymentSuccessOverlayProps {
    visible: boolean;
    title?: string;
    subtitle?: string;
    secondaryLine?: string | null;
    onDone?: () => void;
    durationMs?: number;
    fireConfetti?: boolean;
}

export const WebPaymentSuccessOverlay: React.FC<WebPaymentSuccessOverlayProps> = ({
    visible,
    title = 'Payment initiated',
    subtitle = "We're processing your payment.",
    secondaryLine = null,
    onDone,
    durationMs = 1500,
    fireConfetti = true,
}) => {
    const [mounted, setMounted] = useState(visible);
    const [isClosing, setIsClosing] = useState(false);
    const doneRef = useRef(false);
    const onDoneRef = useRef(onDone);

    useEffect(() => {
        onDoneRef.current = onDone;
    }, [onDone]);

    useEffect(() => {
        if (visible) {
            setMounted(true);
            setIsClosing(false);
            doneRef.current = false;

            if (fireConfetti) {
                try {
                    confetti({
                        particleCount: 120,
                        spread: 70,
                        origin: { y: 0.55 },
                        colors: ['#0d9488', '#2dd4bf', '#14b8a6', '#0f766e', '#ffffff'],
                    });
                } catch {
                    // ignore confetti errors
                }
            }

            const timer = setTimeout(() => {
                handleDismiss();
            }, durationMs);

            return () => clearTimeout(timer);
        } else {
            setMounted(false);
            setIsClosing(false);
        }
    }, [visible, durationMs, fireConfetti]);

    const handleDismiss = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setIsClosing(true);
        setTimeout(() => {
            setMounted(false);
            setIsClosing(false);
            onDoneRef.current?.();
        }, 180);
    };

    if (!mounted) return null;

    return (
        <div
            className={`fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/40 backdrop-blur-md transition-opacity duration-200 ${
                isClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-fadeIn'
            }`}
            onClick={handleDismiss}
        >
            <div
                className={`bg-white rounded-[2rem] p-7 max-w-[320px] w-full shadow-2xl flex flex-col items-center text-center space-y-3.5 transition-all duration-200 transform ${
                    isClosing ? 'scale-95 opacity-0 translate-y-2' : 'scale-100 opacity-100 animate-scaleUp'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Checkmark icon with pulsing teal halo */}
                <div className="relative flex items-center justify-center my-1">
                    <div className="w-16 h-16 rounded-full bg-teal-100/70 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white shadow-md shadow-teal-600/30 animate-scaleUp">
                            <Check className="w-6 h-6 stroke-[3]" />
                        </div>
                    </div>
                    <div className="absolute inset-0 rounded-full bg-teal-500/20 animate-ping pointer-events-none" style={{ animationDuration: '1.4s' }} />
                </div>

                <div className="space-y-1">
                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                        {title}
                    </h3>
                    <p className="text-xs font-semibold text-slate-500 leading-relaxed max-w-[240px] mx-auto">
                        {subtitle}
                    </p>
                    {secondaryLine && (
                        <p className="text-[11px] font-medium text-slate-400">
                            {secondaryLine}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebPaymentSuccessOverlay;
